import type { FormEvent } from 'react';
import { Button } from '@shared/ui/Button';
import {
  ButtonGroup,
  CheckboxLabel,
  FormBox,
  FormErrorMessage,
  FormGrid,
  FormTitle,
  Input,
  Select,
} from './styles';

interface AddDebtModalProps {
  instType: string;
  setInstType: (v: string) => void;
  contractNo: string;
  setContractNo: (v: string) => void;
  creditor: string;
  setCreditor: (v: string) => void;
  title: string;
  setTitle: (v: string) => void;
  totalLimit: string;
  setTotalLimit: (v: string) => void;
  principalBalance: string;
  setPrincipalBalance: (v: string) => void;
  rateType: string;
  setRateType: (v: string) => void;
  baseRate: string;
  setBaseRate: (v: string) => void;
  marginRate: string;
  setMarginRate: (v: string) => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  leasedAssetName: string;
  setLeasedAssetName: (v: string) => void;
  leasedAssetCount: string;
  setLeasedAssetCount: (v: string) => void;
  collateral: string;
  setCollateral: (v: string) => void;
  covenants: string;
  setCovenants: (v: string) => void;
  autoGenerateSchedule: boolean;
  setAutoGenerateSchedule: (v: boolean) => void;
  isCreating: boolean;
  errorMsg: string | null;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

export function AddDebtModal({
  instType,
  setInstType,
  contractNo,
  setContractNo,
  creditor,
  setCreditor,
  title,
  setTitle,
  totalLimit,
  setTotalLimit,
  principalBalance,
  setPrincipalBalance,
  rateType,
  setRateType,
  baseRate,
  setBaseRate,
  marginRate,
  setMarginRate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  leasedAssetName,
  setLeasedAssetName,
  leasedAssetCount,
  setLeasedAssetCount,
  collateral,
  setCollateral,
  covenants,
  setCovenants,
  autoGenerateSchedule,
  setAutoGenerateSchedule,
  isCreating,
  errorMsg,
  onSubmit,
  onClose,
}: AddDebtModalProps) {
  return (
    <FormBox as="form" onSubmit={onSubmit}>
      <FormTitle>
        Новый кредитный или лизинговый договор (с автогенерацией помесячного графика платежей)
      </FormTitle>
      <FormGrid>
        <Select
          value={instType}
          onChange={(e) => {
            const next = e.target.value;
            setInstType(next);
            if (next === 'bank_guarantee') {
              setRateType('fixed');
              setBaseRate('2.2');
              setMarginRate('0');
              setPrincipalBalance('0');
              setAutoGenerateSchedule(true);
            } else {
              setAutoGenerateSchedule(true);
            }
          }}
        >
          <option value="credit_line_vkl">ВКЛ (возобновляемая кредитная линия)</option>
          <option value="project_finance">НКЛ / Проектное финансирование</option>
          <option value="term_loan">Срочный кредит</option>
          <option value="leasing">Лизинг спецтехники / оборудования</option>
          <option value="bank_guarantee">Банковская гарантия</option>
        </Select>
        <Input
          placeholder="№ договора (напр. ВКЛ-2026/12)"
          value={contractNo}
          onChange={(e) => setContractNo(e.target.value)}
          required
        />
        <Input
          placeholder={
            instType === 'bank_guarantee' ? 'Банк-гарант' : 'Кредитор / Лизингодатель'
          }
          value={creditor}
          onChange={(e) => setCreditor(e.target.value)}
          required
        />
        <Input
          placeholder="Наименование / цель финансирования"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <Input
          type="number"
          placeholder={
            instType === 'bank_guarantee'
              ? 'Лимит гарантии (забалансовое обязательство), ₽'
              : 'Лимит договора, ₽'
          }
          value={totalLimit}
          onChange={(e) => setTotalLimit(e.target.value)}
          required
        />
        <Input
          type="number"
          placeholder={
            instType === 'bank_guarantee'
              ? 'Остаток долга (для БГ 0 ₽)'
              : 'Текущий остаток долга, ₽'
          }
          value={principalBalance}
          onChange={(e) => setPrincipalBalance(e.target.value)}
        />
        <Select value={rateType} onChange={(e) => setRateType(e.target.value)}>
          <option value="floating_cbr">Плавающая (КС ЦБ + маржа)</option>
          <option value="fixed">Фиксированная ставка</option>
          <option value="subsidized">Льготная субсидируемая</option>
        </Select>
        <Input
          type="number"
          step="0.1"
          placeholder={
            instType === 'bank_guarantee' ? 'Ставка комиссии БГ, %' : 'Базовая ставка, %'
          }
          value={baseRate}
          onChange={(e) => setBaseRate(e.target.value)}
        />
        <Input
          type="number"
          step="0.1"
          placeholder="Маржа банка, % (для КС ЦБ)"
          value={marginRate}
          onChange={(e) => setMarginRate(e.target.value)}
        />
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        {instType === 'leasing' && (
          <>
            <Input
              placeholder="Предмет лизинга (техника)"
              value={leasedAssetName}
              onChange={(e) => setLeasedAssetName(e.target.value)}
            />
            <Input
              type="number"
              placeholder="Кол-во единиц техники"
              value={leasedAssetCount}
              onChange={(e) => setLeasedAssetCount(e.target.value)}
            />
          </>
        )}
        <Input
          placeholder="Залог / Обеспечение"
          value={collateral}
          onChange={(e) => setCollateral(e.target.value)}
        />
        <Input
          placeholder="Ковенанты банка"
          value={covenants}
          onChange={(e) => setCovenants(e.target.value)}
        />
      </FormGrid>
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={autoGenerateSchedule}
          onChange={(e) => setAutoGenerateSchedule(e.target.checked)}
        />
        <span>
          {instType === 'bank_guarantee'
            ? 'Сформировать график ежеквартальных комиссий по БГ (без погашения основного долга)'
            : 'Автоматически построить помесячный график погашения основного долга и процентов'}
        </span>
      </CheckboxLabel>
      {errorMsg && <FormErrorMessage>{errorMsg}</FormErrorMessage>}
      <ButtonGroup>
        <Button $size="sm" type="submit" disabled={isCreating}>
          {isCreating
            ? 'Создание…'
            : autoGenerateSchedule
              ? 'Создать договор и построить график'
              : 'Создать договор без графика'}
        </Button>
        <Button
          $size="sm"
          $variant="ghost"
          type="button"
          onClick={onClose}
        >
          Отмена
        </Button>
      </ButtonGroup>
    </FormBox>
  );
}
